import axios from 'axios'
import toast from 'react-hot-toast'

// Create axios instance
const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Show success toast for certain operations
    if (response.config.method !== 'get' && response.data.success && response.data.message) {
      toast.success(response.data.message)
    }
    return response
  },
  (error) => {
    let errorMessage = 'An unexpected error occurred'

    if (error.response) {
      // Server responded with error status
      const { data, status } = error.response

      if (status === 401) {
        // Check if this is a login/register request - don't redirect for those
        const isAuthRequest = error.config?.url?.includes('/auth/login') || 
                             error.config?.url?.includes('/auth/register')
        
        if (!isAuthRequest) {
          // Unauthorized - clear token and redirect to login (but not for login/register requests)
          localStorage.removeItem('token')
          window.location.href = '/login'
          errorMessage = 'Session expired. Please log in again.'
        } else {
          // For login/register requests, just get the error message
          errorMessage = data.message || 'Invalid credentials'
        }
      } else if (status === 403) {
        errorMessage = 'Access denied. You do not have permission for this action.'
      } else if (status === 404) {
        errorMessage = 'Requested resource not found.'
      } else if (status === 429) {
        errorMessage = 'Too many requests. Please try again later.'
      } else if (status >= 500) {
        errorMessage = 'Server error. Please try again later.'
      } else if (data.message) {
        errorMessage = data.message
      } else if (data.errors && Array.isArray(data.errors)) {
        errorMessage = data.errors.map((err: any) => err.message).join(', ')
      }
    } else if (error.request) {
      // Network error
      errorMessage = 'Network error. Please check your connection.'
    } else {
      // Something else happened
      errorMessage = error.message || 'An unexpected error occurred'
    }

    // Show error toast for all errors except 401 on non-auth endpoints (handled above)
    const isAuthRequest = error.config?.url?.includes('/auth/login') || 
                         error.config?.url?.includes('/auth/register')
    if (error.response?.status !== 401 || isAuthRequest) {
      // Don't show toast for login/register errors - let the component handle it
      if (!isAuthRequest) {
        toast.error(errorMessage)
      }
    }

    return Promise.reject(error)
  }
)

// Image upload functions
export const uploadIssueImages = async (issueId: string, images: File[]) => {
  const formData = new FormData()
  images.forEach((image) => {
    formData.append('images', image)
  })

  const response = await api.post(`/issues/${issueId}/images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

export const deleteIssueImage = async (issueId: string, imageId: string) => {
  const response = await api.delete(`/issues/${issueId}/images/${imageId}`)
  return response.data
}

// Avatar upload function
export const uploadAvatar = async (avatar: File) => {
  const formData = new FormData()
  formData.append('avatar', avatar)

  const response = await api.post('/auth/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

// Reports API
export const getUsersTasksByProject = async (projectId?: string, department?: string, blocked?: boolean) => {
  const params: any = {}
  if (projectId) params.projectId = projectId
  if (department) params.department = department
  if (blocked !== undefined) params.blocked = blocked
  const response = await api.get('/reports/users-tasks-by-project', { params })
  return response.data
}

// Cheap, non-role-widening dropdown data for report filters - see
// backend/routes/reports.js (added to fix the Reports page firing the heavy
// users-tasks-by-project aggregation 3x per load, twice just for dropdowns)
export const getReportsProjectsList = async () => {
  const response = await api.get('/reports/projects-list')
  return response.data
}

export const getReportsDepartmentsList = async () => {
  const response = await api.get('/reports/departments-list')
  return response.data
}

export const getDailyUserActivity = async (fromDate?: string, toDate?: string, userId?: string) => {
  const params: any = {}
  if (fromDate) params.fromDate = fromDate
  if (toDate) params.toDate = toDate
  if (userId) params.userId = userId
  const response = await api.get('/reports/daily-user-activity', { params })
  return response.data
}

// Filtered Issues Report API
export const getFilteredIssues = async (filters: {
  projectId?: string
  statusCategory?: string | string[]
  issueType?: string | string[]
  fromDate?: string
  toDate?: string
  search?: string
}) => {
  const params: any = {}
  if (filters.projectId) params.projectId = filters.projectId
  if (filters.statusCategory) {
    // Axios will automatically handle arrays in params
    params.statusCategory = filters.statusCategory
  }
  if (filters.issueType) {
    // Axios will automatically handle arrays in params
    params.issueType = filters.issueType
  }
  if (filters.fromDate) params.fromDate = filters.fromDate
  if (filters.toDate) params.toDate = filters.toDate
  if (filters.search) params.search = filters.search
  const response = await api.get('/reports/filtered-issues', { params })
  return response.data
}

// Blocked Issues Report API
// page/limit are optional - omit both to get every matching issue unpaginated
// (used for PDF export, which must always export everything).
export const getBlockedIssues = async (projectId?: string, page?: number, limit?: number) => {
  const params: any = {}
  if (projectId) params.projectId = projectId
  if (page) params.page = page
  if (limit) params.limit = limit
  const response = await api.get('/reports/blocked-issues', { params })
  return response.data
}

// Work Log API functions
export interface WorkLog {
  _id: string
  issue: string | { _id: string; key: string; title: string }
  user: {
    _id: string
    name: string
    email: string
    avatar?: string
  }
  timeSpent: number // in minutes
  timeRemaining?: number // in minutes
  comment?: string
  loggedDate: string
  createdAt: string
  updatedAt: string
}

export interface CreateWorkLogData {
  timeSpent: string // Format: "2w 4d 6h 45m"
  timeRemaining?: string // Format: "2w 4d 6h 45m"
  comment?: string
  loggedDate?: string
}

export const createWorkLog = async (issueId: string, data: CreateWorkLogData) => {
  const response = await api.post(`/issues/${issueId}/worklogs`, data)
  return response.data
}

export const getWorkLogs = async (issueId: string) => {
  const response = await api.get(`/issues/${issueId}/worklogs`)
  return response.data
}

export const updateWorkLog = async (issueId: string, workLogId: string, data: Partial<CreateWorkLogData>) => {
  const response = await api.put(`/issues/${issueId}/worklogs/${workLogId}`, data)
  return response.data
}

export const deleteWorkLog = async (issueId: string, workLogId: string) => {
  const response = await api.delete(`/issues/${issueId}/worklogs/${workLogId}`)
  return response.data
}

export default api
